import {
  Authenticator,
  CheckboxField,
  useAuthenticator,
  View,
} from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import { OidcProvider, OidcSecure } from "@axa-fr/react-oidc";
import { Anchor, AppShell, Image, Loader } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconApps,
  IconArrowBack,
  IconBook,
  IconBrandPagekit,
  IconCalendarEvent,
  IconCategory,
  IconChairDirector,
  IconChartBar,
  IconCreditCard,
  IconDashboard,
  IconFileText,
  IconGauge,
  IconGavel,
  IconGlobe,
  IconHome,
  IconLayoutBoard,
  IconMail,
  IconNotebook,
  IconPaint,
  IconPuzzle,
  IconPlanet,
  IconQrcode,
  IconScript,
  IconSend,
  IconSettings,
  IconShoppingBag,
  IconStar,
  IconTicket,
  IconTrophy,
  IconUserBolt,
  IconDatabase,
  IconTag,
  IconUserCircle,
  IconUsers,
  IconUsersGroup,
  IconUserStar,
  IconWebhook,
} from "@tabler/icons-react";
import * as Sentry from "@sentry/react";
import { Amplify } from "aws-amplify";
import { fetchAuthSession } from "aws-amplify/auth";
import { useCallback, useEffect } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useParams,
} from "react-router-dom";
import { useCurrentAccount } from "../../business/CurrentAccount";
import { IS_LOCAL, IS_STG } from "../../server-select";
import { RouteExtensionPoints } from "./components/app-extensions/RouteExtensionPoints";
import { HeaderMegaMenu } from "./components/app-shell/header/Header";
import { ServiceWithMenuLayer } from "./components/app-shell/ServiceWithMenuLayer";
import { Dashboard } from "./dashboard/Dashboard";
import { PageNotFound } from "./screens/PageNotFound";
import { ServiceLinks } from "./ServiceLinks";
import { CreateOrUpdateEmailTemplate } from "./services/emails/Email";
import { EmailConfiguration } from "./services/emails/EmailConfiguration";
import { Emails } from "./services/emails/Emails";
import { EmailsList } from "./services/emails/EmailsList";
import { EmailSend } from "./services/emails/EmailSend";
import { EventDashboard } from "./services/events/EventDashboard";
import { EventDocumentation } from "./services/events/EventDocumentation";
import { EventForm } from "./services/events/EventForm";
import { EventHome } from "./services/events/EventHome";
import { EventList } from "./services/events/EventList";
import { EventQRScanner } from "./services/events/EventQRScanner";
import { EventReservations } from "./services/events/EventReservations";
import { EventTickets } from "./services/events/EventTickets";
import { CustomFieldsSettings as EventCustomFieldsSettings } from "./services/events/settings/CustomFieldsSettings";
import { RegistrationSettings } from "./services/events/settings/RegistrationSettings";
import {
  CreatOrUpdateVenueSettings,
  VenueHome,
  VenueSeatsUpdate,
} from "./services/events/venues/Venue";
import { Venues } from "./services/events/venues/Venues";
import { Currencies } from "./services/gamification/currencies/Currencies";
import { GamificationDocumentation } from "./services/gamification/GamificationDocumentation";
import { GamificationHome } from "./services/gamification/GamificationHome";
import {
  MarketplaceAppDetail,
  MarketplaceHome,
  MarketplaceMyApps,
} from "./services/marketplace/Marketplace";
import {
  DeveloperAnalytics,
  DeveloperApps,
  DeveloperHome,
} from "./services/developer/Developer";
import { CreateMarketplaceApp } from "./services/developer/CreateMarketplaceApp";
import { AppDetail } from "./services/developer/AppDetail";
import { AppReleases } from "./services/developer/AppReleases";
import {
  ItemsCatalogCategories,
  ItemsCatalogItems,
} from "./services/gamification/items-catalog/ItemsCatalog";
import { PurchaseHistory } from "./services/gamification/shops/PurchaseHistory";
import {
  ShopDashboard,
  ShopsCategories,
  ShopsHome,
  ShopsProducts,
} from "./services/gamification/shops/Shops";
import { LeaderboardDocumentation } from "./services/leaderboard/Documentations";
import { LeaderboardDashboard } from "./services/leaderboard/LeaderboardDashboard";
import {
  CurrentLeaderboard,
  Leaderboards,
  LeaderboardsHome,
  UpdateLeaderboard,
} from "./services/leaderboard/Leaderboards";
import { LeaderboardSeasonBuckets } from "./services/leaderboard/LeaderboardSeasonBuckets";
import { LeaderboardSeasons } from "./services/leaderboard/LeaderboardSeasons";
import { LeaderboardWidgets } from "./services/leaderboard/LeaderboardWidgets";
import { Limits, LimitsHome } from "./services/limits/Limits";
import { SellerSettings } from "./services/limits/seller/Seller";
import { Subscription } from "./services/limits/subscription/Subscription";
import { Apps, EditApp } from "./services/organization/app/Apps";
import { DeveloperDocumentation } from "./services/developer/DeveloperDocumentation";
import { Webhooks } from "./services/organization/app/Webhooks";
import { WebhookHome } from "./services/organization/app/webhooks/WebhookHome";
import { WebhookLogs } from "./services/organization/app/webhooks/WebhookLogs";
import { WebhookUpdate } from "./services/organization/app/webhooks/WebhookUpdate";
import {
  AttachApplication,
  AttachedApplications,
} from "./services/organization/iam/applications/Applications";
import { Groups, UpdateGroup } from "./services/organization/iam/group/Groups";
import { IAMHome } from "./services/organization/iam/IAM";
import { IAMDocumentation } from "./services/organization/iam/IAMDocumentation";
import {
  CreateMember,
  Members,
} from "./services/organization/iam/member/Members";
import { OrganizationConfiguration } from "./services/organization/OrganizationConfiguration";
import { OrganizationDocs } from "./services/organization/OrganizationDocs";
import {
  CreateOrganization,
  OrganizationsList,
} from "./services/organization/Organizations";
import { PlatformFileContent } from "./services/platform/files/PlatformFile";
import {
  PlatformFiles,
  PlatformFileUpdate,
} from "./services/platform/files/PlatformFiles";
import {
  Platform,
  PlatformDomains,
  RegisterPlatformDomain,
} from "./services/platform/Platform";
import { PlatformHome } from "./services/platform/PlatformHome";
import {
  CreateOrUpdatePlatform,
  Platforms,
} from "./services/platform/Platforms";
import { PlatformTemplates } from "./services/platform/templates/PlatformTemplates";
import {
  PlatformThemePage,
  PlatformSectionsPage,
  PlatformModulesPage,
} from "./services/platform/PlatformRouteWrappers";
import {
  IdentityProviders,
  UpdateIdentityProvider,
} from "./services/player-profile/identityProvider/IdentityProviders";
import { PlayerView } from "./services/player-profile/Player";
import { PlayerDocumentation } from "./services/player-profile/PlayerDocumentation";
import { PlayersHome, PlayersList } from "./services/player-profile/Players";
import {
  CreateOrUpdateSkillRating,
  SkillRatings,
} from "./services/player-profile/skill-rating/SkillRatings";
import {
  UpdateTournamentCustomFields,
  UpdateTournamentRegistrationRules,
  UpdateTournamentRegistrations,
} from "./services/tournament/settings";
import {
  StepDashboard as TournamentStepHome,
  StepList as TournamentSteps,
  StepForm as UpdateTournamentStep,
} from "./services/tournament/steps";
import { RuleSetOverview } from "./services/tournament/steps/rules/RuleSetOverview";
import { ScoringEditor } from "./services/tournament/steps/rules/ScoringEditor";
import { AdvancementEditor } from "./services/tournament/steps/rules/AdvancementEditor";
import { StructureEditor } from "./services/tournament/steps/rules/StructureEditor";
import { CrossStepEditor } from "./services/tournament/steps/rules/CrossStepEditor";
import { PresetScriptDetail } from "./services/tournament/preset-scripts/PresetScriptDetail";
import { PresetScriptForm } from "./services/tournament/preset-scripts/PresetScriptForm";
import { PresetScriptsHome } from "./services/tournament/preset-scripts/PresetScriptsHome";
import { PresetScriptsList } from "./services/tournament/preset-scripts/PresetScriptsList";
import { TournamentAdmins } from "./services/tournament/TournamentAdmins";
import { TournamentDashboard } from "./services/tournament/TournamentDashboard";
import { TournamentDocs } from "./services/tournament/TournamentDocs";
import { TournamentLuaReference } from "./services/tournament/TournamentLuaReference";
import { UpdateTournament } from "./services/tournament/TournamentForm";
import { TournamentHome } from "./services/tournament/TournamentHome";
import { TournamentList } from "./services/tournament/TournamentList";
import { TournamentTeams } from "./services/tournament/TournamentTeams";
import { JournalService } from "./services/journal/JournalService";
import { CustomFieldDefinitionCreate } from "./services/data/definitions/CustomFieldDefinitionCreate";
import { CustomFieldDefinitionEdit } from "./services/data/definitions/CustomFieldDefinitionEdit";
import { CustomFieldDefinitions } from "./services/data/definitions/CustomFieldDefinitions";
import { DataDocumentation } from "./services/data/DataDocumentation";
import { DataHome } from "./services/data/DataHome";
import { CustomFieldValuesPage } from "./services/data/values/CustomFieldValuesPage";
import { ObjectMetadataPage } from "./services/data/metadata/ObjectMetadataPage";
import "./styles/login.css";

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: "eu-west-3_05Tcmw8bn",
      userPoolClientId:
        IS_LOCAL || IS_STG
          ? "20h9k2luca7ibf9sq6moh30mop"
          : "1ics32krcep4s9cafqdbursvhp",
    },
  },
});

const ErrorFallback = ({
  resetError,
}: {
  error?: Error;
  resetError: () => void;
}) => (
  <div style={{ padding: 40, textAlign: "center" }}>
    <h2>Something went wrong</h2>
    <p style={{ color: "#666", marginBottom: 20 }}>
      An unexpected error occurred. Please try reloading the page.
    </p>
    <button
      onClick={resetError}
      style={{
        padding: "10px 24px",
        fontSize: 14,
        cursor: "pointer",
        borderRadius: 4,
        border: "1px solid #ccc",
        background: "#228be6",
        color: "#fff",
      }}
    >
      Reload
    </button>
  </div>
);

const LayoutWithSidebar = () => {
  const { account, loading } = useCurrentAccount();

  useEffect(() => {
    if (account?.getMyAccount.email) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      $crisp.push(["set", "user:email", [account.getMyAccount.email]]);
    }
  }, [account]);

  const handleReset = useCallback(() => {
    window.location.reload();
  }, []);

  if (loading) {
    return <Loader />;
  }

  return (
    <AppShell header={{ height: 60 }}>
      <AppShell.Header
        style={{
          backgroundColor: "var(--mantine-color-dark-7)",
          borderBottom: "1px solid var(--mantine-color-dark-5)",
        }}
      >
        <HeaderMegaMenu links={ServiceLinks} />
      </AppShell.Header>
      <AppShell.Main>
        <Sentry.ErrorBoundary
          fallback={() => <ErrorFallback resetError={handleReset} />}
        >
          <Outlet />
        </Sentry.ErrorBoundary>
        <RouteExtensionPoints />
      </AppShell.Main>
    </AppShell>
  );
};

const OAuthClientRedirect = () => {
  const { appId } = useParams();
  return <Navigate to={`/developer/apps/${appId ?? ""}`} replace />;
};

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <OidcSecure configurationName="wp">
        <LayoutWithSidebar />
      </OidcSecure>
    ),
    errorElement: <PageNotFound />,
    children: [
      {
        path: "/",
        element: <Dashboard />,
      },
      {
        path: "/organizations",
        element: (
          <ServiceWithMenuLayer
            navbarConfiguration={{
              title: "Organizations",
              items: [
                {
                  icon: IconHome,
                  label: "Home",
                  path: "/organizations",
                },
                {
                  icon: IconUserCircle,
                  label: "Organizations",
                  path: "/organizations/list",
                },
              ],
            }}
          />
        ),
        children: [
          {
            path: "",
            element: <OrganizationDocs />,
          },
          {
            path: "list",
            element: <OrganizationsList />,
          },
          {
            path: "new",
            element: <CreateOrganization />,
          },
          {
            path: ":organizationId",
            element: <OrganizationConfiguration />,
          },
        ],
      },
      {
        path: "/emails",
        element: (
          <ServiceWithMenuLayer
            navbarConfiguration={{
              title: "Emails",
              items: [
                {
                  icon: IconHome,
                  label: "Home",
                  path: "/emails",
                },
                {
                  icon: IconMail,
                  label: "Templates",
                  path: "/emails/templates",
                },
                {
                  icon: IconSend,
                  label: "Send",
                  path: "/emails/send",
                },
                {
                  icon: IconSettings,
                  label: "Configuration",
                  path: "/emails/configuration",
                },
              ],
            }}
          />
        ),
        children: [
          {
            path: "",
            element: <Emails />,
          },
          {
            path: "templates",
            element: <EmailsList />,
          },
          {
            path: "templates/new",
            element: <CreateOrUpdateEmailTemplate />,
          },
          {
            path: "templates/:templateId",
            element: <CreateOrUpdateEmailTemplate />,
          },
          {
            path: "send",
            element: <EmailSend />,
          },
          {
            path: "configuration",
            element: <EmailConfiguration />,
          },
        ],
      },
      {
        path: "/apps",
        element: <Navigate to="/developer/apps" replace />,
      },
      {
        path: "/apps/*",
        element: <Navigate to="/developer/apps" replace />,
      },
      {
        path: "/developer/webhooks/:webhookId",
        element: (
          <ServiceWithMenuLayer
            navbarConfiguration={{
              title: "Webhook",
              items: [
                {
                  icon: IconArrowBack,
                  label: "Back",
                  path: "/developer/webhooks",
                },
                {
                  icon: IconHome,
                  label: "Home",
                  path: "/developer/webhooks/:webhookId",
                },
                {
                  icon: IconSettings,
                  label: "Configuration",
                  path: "/developer/webhooks/:webhookId/configuration",
                },
                {
                  icon: IconFileText,
                  label: "Logs",
                  path: "/developer/webhooks/:webhookId/logs",
                },
                {
                  icon: IconDatabase,
                  label: "Custom Field Values",
                  path: "/developer/webhooks/:webhookId/custom-field-values",
                },
                {
                  icon: IconTag,
                  label: "Metadata",
                  path: "/developer/webhooks/:webhookId/metadata",
                },
              ],
            }}
          />
        ),
        children: [
          {
            path: "",
            element: <WebhookHome />,
          },
          {
            path: "configuration",
            element: <WebhookUpdate />,
          },
          {
            path: "logs",
            element: <WebhookLogs />,
          },
          {
            path: "custom-field-values",
            element: (
              <CustomFieldValuesPage
                objectType="Webhook"
                paramName="webhookId"
              />
            ),
          },
          {
            path: "metadata",
            element: (
              <ObjectMetadataPage objectType="Webhook" paramName="webhookId" />
            ),
          },
        ],
      },
      {
        path: "/iam",
        element: (
          <ServiceWithMenuLayer
            navbarConfiguration={{
              title: "IAM",
              items: [
                {
                  icon: IconHome,
                  label: "Home",
                  path: "/iam",
                },
                {
                  icon: IconUsersGroup,
                  label: "Groups",
                  path: "/iam/groups",
                },
                {
                  icon: IconUsers,
                  label: "Members",
                  path: "/iam/members",
                },
                {
                  icon: IconApps,
                  label: "Applications",
                  path: "/iam/applications",
                },
                {
                  icon: IconBook,
                  label: "Documentations",
                  path: "/iam/documentations",
                },
              ],
            }}
          />
        ),
        children: [
          {
            path: "",
            element: <IAMHome />,
          },
          {
            path: "groups",
            element: <Groups />,
          },
          {
            path: "groups/new",
            element: <UpdateGroup />,
          },
          {
            path: "groups/:groupId",
            element: <UpdateGroup />,
          },
          {
            path: "members",
            element: <Members />,
          },
          {
            path: "members/new",
            element: <CreateMember />,
          },
          {
            path: "members/:memberId",
            element: <CreateMember />,
          },
          {
            path: "applications",
            element: <AttachedApplications />,
          },
          {
            path: "applications/new",
            element: <AttachApplication />,
          },
          {
            path: "documentations",
            element: <IAMDocumentation />,
          },
        ],
      },
      {
        path: "/tournaments",
        element: (
          <ServiceWithMenuLayer
            navbarConfiguration={{
              title: "Tournaments",
              items: [
                {
                  icon: IconHome,
                  label: "Home",
                  path: "/tournaments",
                },
                {
                  icon: IconUsersGroup,
                  label: "Tournaments",
                  path: "/tournaments/list",
                },
                {
                  icon: IconUsers,
                  label: "Series",
                  path: "/tournaments/series",
                  disabled: true,
                },
                {
                  icon: IconScript,
                  label: "Preset Scripts",
                  path: "/tournaments/preset-scripts",
                },
                {
                  icon: IconBook,
                  label: "Documentation",
                  path: "/tournaments/docs",
                },
                {
                  icon: IconScript,
                  label: "Lua API Reference",
                  path: "/tournaments/docs/lua-reference",
                },
              ],
            }}
          />
        ),
        children: [
          {
            path: "",
            element: <TournamentHome />,
          },
          {
            path: "list",
            element: <TournamentList />,
          },
          {
            path: "series",
            element: <>Not available yet</>,
          },
          {
            path: "new",
            element: <UpdateTournament />,
          },
          {
            path: "docs",
            element: <TournamentDocs />,
          },
          {
            path: "docs/lua-reference",
            element: <TournamentLuaReference />,
          },
        ],
      },
      {
        path: "/tournaments/preset-scripts",
        element: (
          <ServiceWithMenuLayer
            navbarConfiguration={{
              title: "Preset Scripts",
              items: [
                {
                  icon: IconArrowBack,
                  label: "Tournaments",
                  path: "/tournaments",
                },
                {
                  icon: IconHome,
                  label: "Home",
                  path: "/tournaments/preset-scripts",
                },
                {
                  icon: IconScript,
                  label: "All Presets",
                  path: "/tournaments/preset-scripts/list",
                },
              ],
            }}
          />
        ),
        children: [
          {
            path: "",
            element: <PresetScriptsHome />,
          },
          {
            path: "list",
            element: <PresetScriptsList showHeader />,
          },
          {
            path: "new",
            element: <PresetScriptForm />,
          },
          {
            path: ":presetScriptId",
            element: <PresetScriptDetail />,
          },
          {
            path: ":presetScriptId/edit",
            element: <PresetScriptForm />,
          },
        ],
      },
      {
        path: "/tournaments/:tournamentId",
        element: (
          <ServiceWithMenuLayer
            navbarConfiguration={{
              title: "Tournament",
              items: [
                {
                  icon: IconArrowBack,
                  label: "Back",
                  path: "/tournaments/list",
                },
                {
                  icon: IconDashboard,
                  label: "Dashboard",
                  path: "/tournaments/:tournamentId",
                },
                {
                  icon: IconUsersGroup,
                  label: "Teams",
                  path: "/tournaments/:tournamentId/teams",
                },
                {
                  icon: IconSettings,
                  label: "Settings",
                  children: [
                    {
                      label: "General",
                      path: "/tournaments/:tournamentId/settings/general",
                    },
                    {
                      label: "Registration",
                      path: "/tournaments/:tournamentId/settings/registration",
                    },
                    {
                      label: "Registration Rules",
                      path: "/tournaments/:tournamentId/settings/registration-rules",
                    },
                    {
                      label: "Custom Fields",
                      path: "/tournaments/:tournamentId/settings/custom-fields",
                    },
                    {
                      label: "Custom Field Values",
                      path: "/tournaments/:tournamentId/settings/custom-field-values",
                    },
                    {
                      label: "Metadata",
                      path: "/tournaments/:tournamentId/settings/metadata",
                    },
                  ],
                },
                {
                  icon: IconChartBar,
                  label: "Steps",
                  path: "/tournaments/:tournamentId/steps",
                },
                {
                  icon: IconUserStar,
                  label: "Admins",
                  path: "/tournaments/:tournamentId/admins",
                },
                {
                  icon: IconApps,
                  label: "Widgets",
                  path: "/tournaments/:tournamentId/widgets",
                  disabled: true,
                },
              ],
            }}
          />
        ),
        children: [
          {
            path: "",
            element: <TournamentDashboard />,
          },
          {
            path: "settings/general",
            element: <UpdateTournament />,
          },
          {
            path: "settings/registration",
            element: <UpdateTournamentRegistrations />,
          },
          {
            path: "settings/registration-rules",
            element: <UpdateTournamentRegistrationRules />,
          },
          {
            path: "settings/custom-fields",
            element: <UpdateTournamentCustomFields />,
          },
          {
            path: "settings/custom-field-values",
            element: (
              <CustomFieldValuesPage
                objectType="Tournament"
                paramName="tournamentId"
              />
            ),
          },
          {
            path: "settings/metadata",
            element: (
              <ObjectMetadataPage
                objectType="Tournament"
                paramName="tournamentId"
              />
            ),
          },
          {
            path: "teams",
            element: <TournamentTeams />,
          },
          {
            path: "steps",
            element: <TournamentSteps />,
          },
          {
            path: "steps/new",
            element: <UpdateTournamentStep />,
          },
          {
            path: "admins",
            element: <TournamentAdmins />,
          },
        ],
      },
      {
        path: "/tournaments/:tournamentId/steps/:stepId",
        element: (
          <ServiceWithMenuLayer
            navbarConfiguration={{
              title: "Tournament Step",
              items: [
                {
                  icon: IconArrowBack,
                  label: "Back",
                  path: "/tournaments/:tournamentId/steps",
                },
                {
                  icon: IconChartBar,
                  label: "Step Info",
                  path: "/tournaments/:tournamentId/steps/:stepId",
                },
                {
                  icon: IconSettings,
                  label: "Settings",
                  path: "/tournaments/:tournamentId/steps/:stepId/settings",
                },
                {
                  icon: IconGavel,
                  label: "Rules",
                  path: "/tournaments/:tournamentId/steps/:stepId/rules",
                },
                {
                  icon: IconDatabase,
                  label: "Custom Field Values",
                  path: "/tournaments/:tournamentId/steps/:stepId/custom-field-values",
                },
                {
                  icon: IconTag,
                  label: "Metadata",
                  path: "/tournaments/:tournamentId/steps/:stepId/metadata",
                },
              ],
            }}
          />
        ),
        children: [
          {
            path: "",
            element: <TournamentStepHome />,
          },
          {
            path: "settings",
            element: <UpdateTournamentStep />,
          },
          {
            path: "rules",
            element: <RuleSetOverview />,
          },
          {
            path: "rules/scoring",
            element: <ScoringEditor />,
          },
          {
            path: "rules/advancement",
            element: <AdvancementEditor />,
          },
          {
            path: "rules/structure",
            element: <StructureEditor />,
          },
          {
            path: "rules/cross-step",
            element: <CrossStepEditor />,
          },
          {
            path: "custom-field-values",
            element: (
              <CustomFieldValuesPage
                objectType="TournamentStep"
                paramName="stepId"
              />
            ),
          },
          {
            path: "metadata",
            element: (
              <ObjectMetadataPage
                objectType="TournamentStep"
                paramName="stepId"
              />
            ),
          },
        ],
      },
      {
        path: "/players",
        element: (
          <ServiceWithMenuLayer
            navbarConfiguration={{
              title: "Players",
              items: [
                {
                  icon: IconHome,
                  label: "Home",
                  path: "/players",
                },
                {
                  icon: IconUsersGroup,
                  label: "Players",
                  path: "/players/list",
                },
                {
                  icon: IconUserBolt,
                  label: "Identity Providers",
                  path: "/players/idp",
                },
                {
                  icon: IconStar,
                  label: "Skill Ratings",
                  path: "/players/mmr",
                },
                {
                  icon: IconBook,
                  label: "Documentations",
                  path: "/players/documentations",
                },
              ],
            }}
          />
        ),
        children: [
          {
            path: "",
            element: <PlayersHome />,
          },
          {
            path: "list",
            element: <PlayersList />,
          },
          {
            path: "idp",
            element: <IdentityProviders />,
          },
          {
            path: "idp/new",
            element: <UpdateIdentityProvider />,
          },
          {
            path: "idp/:idpId",
            element: <UpdateIdentityProvider />,
          },
          {
            path: ":playerId",
            element: <PlayerView />,
          },
          {
            path: "mmr",
            element: <SkillRatings />,
          },
          {
            path: "mmr/new",
            element: <CreateOrUpdateSkillRating />,
          },
          {
            path: "mmr/:skillRatingId",
            element: <CreateOrUpdateSkillRating />,
          },
          {
            path: "documentations",
            element: <PlayerDocumentation />,
          },
        ],
      },
      {
        path: "/gaming",
        element: (
          <ServiceWithMenuLayer
            navbarConfiguration={{
              title: "Gaming",
              items: [
                {
                  icon: IconHome,
                  label: "Home",
                  path: "/gaming",
                },
                {
                  icon: IconPlanet,
                  label: "Matchmaking",
                  path: "/gaming/matchmaking",
                  disabled: true,
                },
              ],
            }}
          />
        ),
        children: [
          {
            path: "",
            element: <>Gaming Services</>,
          },
          {
            path: "matchmaking",
            element: <>Coming soon</>,
          },
        ],
      },
      {
        path: "/limits",
        element: (
          <ServiceWithMenuLayer
            navbarConfiguration={{
              title: "Limits",
              items: [
                {
                  icon: IconHome,
                  label: "Home",
                  path: "/limits",
                },
                {
                  icon: IconGauge,
                  label: "Limits",
                  path: "/limits/list",
                },
                {
                  icon: IconCreditCard,
                  label: "Subscription",
                  path: "/limits/subscription",
                },
                {
                  icon: IconShoppingBag,
                  label: "Seller settings",
                  path: "/limits/seller",
                },
              ],
            }}
          />
        ),
        children: [
          {
            path: "",
            element: <LimitsHome />,
          },
          {
            path: "list",
            element: <Limits />,
          },
          {
            path: "subscription",
            element: <Subscription />,
          },
          {
            path: "seller",
            element: <SellerSettings />,
          },
        ],
      },
      {
        path: "/leaderboards",
        element: (
          <ServiceWithMenuLayer
            navbarConfiguration={{
              title: "Leaderboards",
              items: [
                {
                  icon: IconHome,
                  label: "Home",
                  path: "/leaderboards",
                },
                {
                  icon: IconGauge,
                  label: "Leaderboards",
                  path: "/leaderboards/list",
                },
                {
                  icon: IconBook,
                  label: "Documentations",
                  path: "/leaderboards/documentations",
                },
              ],
            }}
          />
        ),
        children: [
          {
            path: "",
            element: <LeaderboardsHome />,
          },
          {
            path: "list",
            element: <Leaderboards />,
          },
          {
            path: "new",
            element: <UpdateLeaderboard />,
          },
          {
            path: "documentations",
            element: <LeaderboardDocumentation />,
          },
        ],
      },
      {
        path: "/leaderboards/:leaderboardId",
        element: (
          <ServiceWithMenuLayer
            navbarConfiguration={{
              title: "Leaderboard",
              items: [
                {
                  icon: IconArrowBack,
                  label: "Back",
                  path: "/leaderboards/list",
                },
                {
                  icon: IconDashboard,
                  label: "Dashboard",
                  path: "/leaderboards/:leaderboardId",
                },
                {
                  icon: IconTrophy,
                  label: "Current Leaderboard",
                  path: "/leaderboards/:leaderboardId/current",
                },
                {
                  icon: IconCalendarEvent,
                  label: "Seasons",
                  path: "/leaderboards/:leaderboardId/seasons",
                },
                {
                  icon: IconSettings,
                  label: "Settings",
                  path: "/leaderboards/:leaderboardId/settings",
                },
                {
                  icon: IconApps,
                  label: "Widgets",
                  path: "/leaderboards/:leaderboardId/widgets",
                },
                {
                  icon: IconDatabase,
                  label: "Custom Field Values",
                  path: "/leaderboards/:leaderboardId/custom-field-values",
                },
                {
                  icon: IconTag,
                  label: "Metadata",
                  path: "/leaderboards/:leaderboardId/metadata",
                },
              ],
            }}
          />
        ),
        children: [
          {
            path: "",
            element: <LeaderboardDashboard />,
          },
          {
            path: "widgets",
            element: <LeaderboardWidgets />,
          },
          {
            path: "settings",
            element: <UpdateLeaderboard />,
          },
          {
            path: "current",
            element: <CurrentLeaderboard />,
          },
          {
            path: "seasons",
            element: <LeaderboardSeasons />,
          },
          {
            path: "seasons/:seasonId/buckets",
            element: <LeaderboardSeasonBuckets />,
          },
          {
            path: "custom-field-values",
            element: (
              <CustomFieldValuesPage
                objectType="Leaderboard"
                paramName="leaderboardId"
              />
            ),
          },
          {
            path: "metadata",
            element: (
              <ObjectMetadataPage
                objectType="Leaderboard"
                paramName="leaderboardId"
              />
            ),
          },
        ],
      },
      {
        path: "/platform",
        element: (
          <ServiceWithMenuLayer
            navbarConfiguration={{
              title: "Platforms",
              items: [
                {
                  icon: IconHome,
                  label: "Home",
                  path: "/platform",
                },
                {
                  icon: IconBrandPagekit,
                  label: "List",
                  path: "/platform/list",
                },
                {
                  icon: IconPaint,
                  label: "Templates",
                  path: "/platform/templates",
                  disabled: true,
                },
                {
                  icon: IconShoppingBag,
                  label: "Store",
                  path: "/platform/store",
                  disabled: true,
                },
              ],
            }}
          />
        ),
        children: [
          {
            path: "",
            element: <PlatformHome />,
          },
          {
            path: "list",
            element: <Platforms />,
          },
          {
            path: "templates",
            element: <PlatformTemplates />,
          },
          {
            path: "new",
            element: <CreateOrUpdatePlatform />,
          },
        ],
      },
      {
        path: "/platform/:platformId",
        element: (
          <ServiceWithMenuLayer
            navbarConfiguration={{
              title: "Platform",
              items: [
                {
                  icon: IconArrowBack,
                  label: "Back",
                  path: "/platform/list",
                },
                {
                  icon: IconHome,
                  label: "Dashboard",
                  path: "/platform/:platformId",
                },
                {
                  icon: IconApps,
                  label: "Files",
                  path: "/platform/:platformId/files",
                },
                {
                  icon: IconGlobe,
                  label: "Custom Domains",
                  path: "/platform/:platformId/domains",
                },
                {
                  icon: IconPaint,
                  label: "Theme",
                  path: "/platform/:platformId/theme",
                },
                {
                  icon: IconLayoutBoard,
                  label: "Sections",
                  path: "/platform/:platformId/sections",
                },
                {
                  icon: IconPuzzle,
                  label: "Modules",
                  path: "/platform/:platformId/modules",
                },
                {
                  icon: IconSettings,
                  label: "Settings",
                  path: "/platform/:platformId/settings",
                },
                {
                  icon: IconDatabase,
                  label: "Custom Field Values",
                  path: "/platform/:platformId/custom-field-values",
                },
                {
                  icon: IconTag,
                  label: "Metadata",
                  path: "/platform/:platformId/metadata",
                },
              ],
            }}
          />
        ),
        children: [
          {
            path: "",
            element: <Platform />,
          },
          {
            path: "settings",
            element: <CreateOrUpdatePlatform />,
          },
          {
            path: "files",
            element: <PlatformFiles />,
          },
          {
            path: "files/new",
            element: <PlatformFileUpdate />,
          },
          {
            path: "files/:fileId",
            element: <PlatformFileUpdate />,
          },
          {
            path: "domains",
            element: <PlatformDomains />,
          },
          {
            path: "domains/new",
            element: <RegisterPlatformDomain />,
          },
          {
            path: "theme",
            element: <PlatformThemePage />,
          },
          {
            path: "sections",
            element: <PlatformSectionsPage />,
          },
          {
            path: "modules",
            element: <PlatformModulesPage />,
          },
          {
            path: "custom-field-values",
            element: (
              <CustomFieldValuesPage
                objectType="Platform"
                paramName="platformId"
              />
            ),
          },
          {
            path: "metadata",
            element: (
              <ObjectMetadataPage
                objectType="Platform"
                paramName="platformId"
              />
            ),
          },
        ],
      },
      {
        path: "/platform/:platformId/files/:fileId",
        element: (
          <ServiceWithMenuLayer
            navbarConfiguration={{
              title: "File",
              items: [
                {
                  icon: IconArrowBack,
                  label: "Back",
                  path: "/platform/:platformId/files",
                },
                {
                  icon: IconApps,
                  label: "Content",
                  path: "/platform/:platformId/files/:fileId",
                },
                {
                  icon: IconSettings,
                  label: "Settings",
                  path: "/platform/:platformId/files/:fileId/settings",
                },
                {
                  icon: IconDatabase,
                  label: "Custom Field Values",
                  path: "/platform/:platformId/files/:fileId/custom-field-values",
                },
                {
                  icon: IconTag,
                  label: "Metadata",
                  path: "/platform/:platformId/files/:fileId/metadata",
                },
              ],
            }}
          />
        ),
        children: [
          {
            path: "",
            element: <PlatformFileContent />,
          },
          {
            path: "settings",
            element: <PlatformFileUpdate />,
          },
          {
            path: "custom-field-values",
            element: (
              <CustomFieldValuesPage
                objectType="PlatformCdnFile"
                paramName="fileId"
              />
            ),
          },
          {
            path: "metadata",
            element: (
              <ObjectMetadataPage
                objectType="PlatformCdnFile"
                paramName="fileId"
              />
            ),
          },
        ],
      },
      {
        path: "/events",
        element: (
          <ServiceWithMenuLayer
            navbarConfiguration={{
              title: "Events",
              items: [
                {
                  icon: IconHome,
                  label: "Home",
                  path: "/events",
                },
                {
                  icon: IconBrandPagekit,
                  label: "List",
                  path: "/events/list",
                },
                {
                  icon: IconTicket,
                  label: "Venues",
                  path: "/events/venues",
                },
                {
                  icon: IconBook,
                  label: "Documentation",
                  path: "/events/documentation",
                },
              ],
            }}
          />
        ),
        children: [
          {
            path: "",
            element: <EventHome />,
          },
          {
            path: "list",
            element: <EventList />,
          },
          {
            path: "new",
            element: <EventForm />,
          },
          {
            path: "documentation",
            element: <EventDocumentation />,
          },
          {
            path: "venues",
            element: <Venues />,
          },
          {
            path: "venues/new",
            element: <CreatOrUpdateVenueSettings />,
          },
        ],
      },
      {
        path: "/events/venues/:venueId",
        element: (
          <ServiceWithMenuLayer
            navbarConfiguration={{
              title: "Venue",
              items: [
                {
                  icon: IconArrowBack,
                  label: "Back",
                  path: "/events/venues",
                },
                {
                  icon: IconHome,
                  label: "Dashboard",
                  path: "/events/venues/:venueId",
                },
                {
                  icon: IconChairDirector,
                  label: "Seats",
                  path: "/events/venues/:venueId/seats",
                },
                {
                  icon: IconDatabase,
                  label: "Custom Field Values",
                  path: "/events/venues/:venueId/custom-field-values",
                },
                {
                  icon: IconTag,
                  label: "Metadata",
                  path: "/events/venues/:venueId/metadata",
                },
              ],
            }}
          />
        ),
        children: [
          {
            path: "",
            element: <VenueHome />,
          },
          {
            path: "settings",
            element: <CreatOrUpdateVenueSettings />,
          },
          {
            path: "seats",
            element: <VenueSeatsUpdate />,
          },
          {
            path: "custom-field-values",
            element: (
              <CustomFieldValuesPage
                objectType="EventVenue"
                paramName="venueId"
              />
            ),
          },
          {
            path: "metadata",
            element: (
              <ObjectMetadataPage objectType="EventVenue" paramName="venueId" />
            ),
          },
        ],
      },
      {
        path: "/events/:eventId",
        element: (
          <ServiceWithMenuLayer
            navbarConfiguration={{
              title: "Events",
              items: [
                {
                  icon: IconArrowBack,
                  label: "Back",
                  path: "/events/list",
                },
                {
                  icon: IconDashboard,
                  label: "Dashboard",
                  path: "/events/:eventId",
                },
                {
                  icon: IconTicket,
                  label: "Reservations",
                  path: "/events/:eventId/reservations",
                },
                {
                  icon: IconQrcode,
                  label: "QR Code Scanner",
                  path: "/events/:eventId/qr-scanner",
                },
                {
                  icon: IconSettings,
                  label: "Settings",
                  children: [
                    {
                      label: "General",
                      path: "/events/:eventId/settings/general",
                    },
                    {
                      label: "Reservations",
                      path: "/events/:eventId/settings/reservations",
                    },
                    {
                      label: "Reservation Rules",
                      path: "/events/:eventId/settings/reservation-rules",
                      disabled: true,
                    },
                    {
                      label: "Custom Fields",
                      path: "/events/:eventId/settings/custom-fields",
                    },
                    {
                      label: "Custom Field Values",
                      path: "/events/:eventId/settings/custom-field-values",
                    },
                    {
                      label: "Metadata",
                      path: "/events/:eventId/settings/metadata",
                    },
                    {
                      label: "Tickets",
                      path: "/events/:eventId/settings/tickets",
                    },
                  ],
                },
              ],
            }}
          />
        ),
        children: [
          {
            path: "",
            element: <EventDashboard />,
          },
          {
            path: "reservations",
            element: <EventReservations />,
          },
          {
            path: "settings/general",
            element: <EventForm />,
          },
          {
            path: "settings/reservations",
            element: <RegistrationSettings />,
          },
          {
            path: "settings/custom-fields",
            element: <EventCustomFieldsSettings />,
          },
          {
            path: "settings/custom-field-values",
            element: (
              <CustomFieldValuesPage objectType="Event" paramName="eventId" />
            ),
          },
          {
            path: "settings/metadata",
            element: (
              <ObjectMetadataPage objectType="Event" paramName="eventId" />
            ),
          },
          {
            path: "settings/tickets",
            element: <EventTickets />,
          },
          {
            path: "qr-scanner",
            element: <EventQRScanner />,
          },
        ],
      },
      {
        path: "/gamification",
        element: (
          <ServiceWithMenuLayer
            navbarConfiguration={{
              title: "Gamification",
              items: [
                {
                  icon: IconHome,
                  label: "Home",
                  path: "/gamification",
                },
                {
                  icon: IconShoppingBag,
                  label: "Items catalog",
                  children: [
                    {
                      label: "Categories",
                      path: "/gamification/items-catalog/categories",
                    },
                    {
                      label: "Items",
                      path: "/gamification/items-catalog/items",
                    },
                  ],
                },
                {
                  icon: IconBrandPagekit,
                  label: "Shops",
                  path: "/gamification/shops",
                },
                {
                  icon: IconCreditCard,
                  label: "Currencies",
                  path: "/gamification/currencies",
                },
                {
                  icon: IconBook,
                  label: "Documentation",
                  path: "/gamification/documentation",
                },
              ],
            }}
          />
        ),
        children: [
          {
            path: "",
            element: <GamificationHome />,
          },
          {
            path: "documentation",
            element: <GamificationDocumentation />,
          },
          {
            path: "items-catalog/categories",
            element: <ItemsCatalogCategories />,
          },
          {
            path: "items-catalog/items",
            element: <ItemsCatalogItems />,
          },
          {
            path: "shops",
            element: <ShopsHome />,
          },
          {
            path: "currencies",
            element: <Currencies />,
          },
        ],
      },
      {
        path: "/gamification/shops/:shopId",
        element: (
          <ServiceWithMenuLayer
            navbarConfiguration={{
              title: "Shop",
              items: [
                {
                  icon: IconArrowBack,
                  label: "Back",
                  path: "/gamification/shops",
                },
                {
                  icon: IconHome,
                  label: "Dashboard",
                  path: "/gamification/shops/:shopId",
                },
                {
                  icon: IconSettings,
                  label: "Settings",
                  path: "/gamification/shops/:shopId/settings",
                  disabled: true,
                },
                {
                  icon: IconShoppingBag,
                  label: "Products",
                  path: "/gamification/shops/:shopId/products",
                },
                {
                  icon: IconCategory,
                  label: "Categories",
                  path: "/gamification/shops/:shopId/categories",
                },
                {
                  icon: IconTicket,
                  label: "Purchases",
                  path: "/gamification/shops/:shopId/purchases",
                },
                {
                  icon: IconDatabase,
                  label: "Custom Field Values",
                  path: "/gamification/shops/:shopId/custom-field-values",
                },
                {
                  icon: IconTag,
                  label: "Metadata",
                  path: "/gamification/shops/:shopId/metadata",
                },
              ],
            }}
          />
        ),
        children: [
          {
            path: "",
            element: <ShopDashboard />,
          },
          {
            path: "products",
            element: <ShopsProducts />,
          },
          {
            path: "categories",
            element: <ShopsCategories />,
          },
          {
            path: "purchases",
            element: <PurchaseHistory />,
          },
          {
            path: "custom-field-values",
            element: (
              <CustomFieldValuesPage objectType="Shop" paramName="shopId" />
            ),
          },
          {
            path: "metadata",
            element: (
              <ObjectMetadataPage objectType="Shop" paramName="shopId" />
            ),
          },
        ],
      },
      {
        path: "/data",
        element: (
          <ServiceWithMenuLayer
            navbarConfiguration={{
              title: "Data",
              items: [
                {
                  icon: IconHome,
                  label: "Home",
                  path: "/data",
                },
                {
                  icon: IconSettings,
                  label: "Custom Fields",
                  path: "/data/custom-fields",
                },
                {
                  icon: IconBook,
                  label: "Documentation",
                  path: "/data/documentations",
                },
              ],
            }}
          />
        ),
        children: [
          {
            path: "",
            element: <DataHome />,
          },
          {
            path: "custom-fields",
            element: <CustomFieldDefinitions />,
          },
          {
            path: "custom-fields/create",
            element: <CustomFieldDefinitionCreate />,
          },
          {
            path: "custom-fields/:id/edit",
            element: <CustomFieldDefinitionEdit />,
          },
          {
            path: "documentations",
            element: <DataDocumentation />,
          },
        ],
      },
      {
        path: "/marketplace",
        element: (
          <ServiceWithMenuLayer
            navbarConfiguration={{
              title: "Marketplace",
              items: [
                {
                  icon: IconHome,
                  label: "Browse",
                  path: "/marketplace",
                },
                {
                  icon: IconApps,
                  label: "Installed Apps",
                  path: "/marketplace/installed",
                },
              ],
            }}
          />
        ),
        children: [
          {
            path: "",
            element: <MarketplaceHome />,
          },
          {
            path: "installed",
            element: <MarketplaceMyApps />,
          },
          {
            path: "browse/:appId",
            element: <MarketplaceAppDetail />,
          },
        ],
      },
      {
        path: "/journal",
        element: (
          <ServiceWithMenuLayer
            navbarConfiguration={{
              title: "Journal",
              items: [
                {
                  icon: IconNotebook,
                  label: "Feed",
                  path: "/journal",
                },
              ],
            }}
          />
        ),
        children: [
          {
            path: "",
            element: <JournalService />,
          },
        ],
      },
      {
        path: "/developer",
        element: (
          <ServiceWithMenuLayer
            navbarConfiguration={{
              title: "Developer",
              items: [
                {
                  icon: IconHome,
                  label: "Home",
                  path: "/developer",
                },
                {
                  icon: IconApps,
                  label: "Apps",
                  path: "/developer/apps",
                },
                {
                  icon: IconPuzzle,
                  label: "Published",
                  path: "/developer/published",
                },
                {
                  icon: IconWebhook,
                  label: "Webhooks",
                  path: "/developer/webhooks",
                },
                {
                  icon: IconChartBar,
                  label: "Analytics",
                  path: "/developer/analytics",
                },
                {
                  icon: IconBook,
                  label: "Docs",
                  path: "/developer/docs",
                },
              ],
            }}
          />
        ),
        children: [
          {
            path: "",
            element: <DeveloperHome />,
          },
          {
            path: "apps",
            children: [
              {
                path: "",
                element: <Apps />,
              },
              {
                path: "new",
                element: <EditApp />,
              },
              {
                path: ":appId",
                element: <EditApp />,
              },
            ],
          },
          {
            path: "published",
            children: [
              {
                path: "",
                element: <DeveloperApps />,
              },
              {
                path: "new",
                element: <CreateMarketplaceApp />,
              },
              {
                path: ":appId",
                element: <AppDetail />,
              },
              {
                path: ":appId/releases",
                element: <AppReleases />,
              },
            ],
          },
          {
            path: "oauth-clients",
            element: <Navigate to="/developer/apps" replace />,
          },
          {
            path: "oauth-clients/new",
            element: <Navigate to="/developer/apps/new" replace />,
          },
          {
            path: "oauth-clients/:appId",
            element: <OAuthClientRedirect />,
          },
          {
            path: "webhooks",
            element: <Webhooks />,
          },
          {
            path: "webhooks/new",
            element: <WebhookUpdate />,
          },
          {
            path: "analytics",
            element: <DeveloperAnalytics />,
          },
          {
            path: "docs",
            element: <DeveloperDocumentation />,
          },
        ],
      },
    ],
  },
]);

export const Layout = () => {
  const { authStatus } = useAuthenticator((context) => [context.route]);
  const url = new URL(window.location.href);
  const challenge = url.searchParams.get("login_challenge");

  useEffect(() => {
    if (authStatus !== "authenticated") return;
    if (!challenge) return;

    fetchAuthSession().then((session) => {
      if (!session.tokens?.idToken) return;

      fetch(
        (IS_LOCAL || IS_STG
          ? "https://idp.warrior.stg.well-played.gg/"
          : "https://idp.warrior.well-played.gg/") + "login/root",
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          method: "POST",
          body: JSON.stringify({
            challenge,
            token: session.tokens.idToken.toString(),
          }),
        },
      )
        .then((data) => data.json())
        .then((body) => {
          const redirectTo = body?.redirectTo;
          if (!redirectTo) {
            notifications.show({
              title: "Error",
              message: JSON.stringify(body),
              variant: "error",
            });
            return;
          }
          window.location.replace(body.redirectTo);
        });
    });
  }, [authStatus]);

  if (authStatus !== "authenticated") {
    return (
      <div className="wp-login-container">
        <div className="wp-login-logo">
          <Image src="/logo-big.png" w={300} alt="WellPlayed Logo" />
        </div>
        <div className="wp-login-tagline">Gaming Platform Management</div>
        <Authenticator
          loginMechanisms={["email"]}
          components={{
            SignUp: {
              FormFields() {
                const { validationErrors } = useAuthenticator();

                return (
                  <>
                    {/* Default Form Fields */}
                    <Authenticator.SignUp.FormFields />

                    {/* Terms and Conditions Checkbox */}
                    <View marginTop="1rem">
                      <CheckboxField
                        name="terms"
                        label={
                          <Anchor
                            href="https://well-played.gg/tos"
                            target="_blank"
                          >
                            I agree to the Terms and Conditions
                          </Anchor>
                        }
                        value="accepted"
                        errorMessage={validationErrors.terms as string}
                        hasError={!!validationErrors.terms}
                        required
                      />
                    </View>
                  </>
                );
              },
            },
          }}
          formFields={{
            signUp: {
              terms: {
                type: "checkbox",
                label: "I agree to the Terms and Conditions",
                required: true,
                labelHidden: true,
              },
            },
          }}
        />
      </div>
    );
  }

  if (challenge) {
    return <>Redirecting...</>;
  }

  return (
    <OidcProvider
      configurationName="wp"
      configuration={{
        client_id: IS_LOCAL
          ? "0713a982-531c-4fac-97db-d501599b57bd"
          : IS_STG
            ? "fd534c2f-6801-4b7d-9dfd-fb9622b19062"
            : "a2af6215-9d0a-442d-87a0-1dea5cfc8ce0",
        redirect_uri: `${window.location.origin}/login/auth`,
        scope: "openid offline_access",
        authority:
          IS_LOCAL || IS_STG
            ? "https://oauth.warrior.stg.well-played.gg"
            : "https://oauth.warrior.well-played.gg",
      }}
    >
      <RouterProvider router={router} />
    </OidcProvider>
  );
};
